import ResultsSection from '@/sections/results'
import type { ThemeEditorSectionUnloadEvent } from '@/types/shopify'

const selectors = {
  grid: '[data-product-grid]'
}

const GRID_STORAGE_KEY = 'featured-products.grid-columns'
const MOBILE_MEDIA_QUERY = '(max-width: 63.98rem)'

type GridMode = '3' | '6' | '2' | '1' | 'list'

export default class CollectionSection extends ResultsSection {
  static TYPE = 'collection'

  grid: HTMLElement | undefined
  currentMode: GridMode
  mobileQuery: MediaQueryList

  onExternalSetColumnsBound: (e: CustomEvent<{ mode?: GridMode; cols?: number }>) => void
  onViewportChangeBound: () => void

  constructor(container: HTMLElement) {
    super(container)

    // The collection grid lives inside the `results-display` component,
    // so query directly from the section container (not BaseSection.qs).
    this.grid = this.container.querySelector(selectors.grid) as HTMLElement | undefined
    this.mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
    this.currentMode = this.normalizeGridMode(this.dataset.defaultCols || '6')
    this.onExternalSetColumnsBound = this.onExternalSetColumns.bind(this)
    this.onViewportChangeBound = this.onViewportChange.bind(this)

    this.initializeGrid()
  }

  onUnload(e: ThemeEditorSectionUnloadEvent) {
    window.removeEventListener('featuredProducts.setColumns', this.onExternalSetColumnsBound as EventListener)
    this.mobileQuery.removeEventListener('change', this.onViewportChangeBound)

    super.onUnload(e)
  }

  isMobileView() {
    return this.mobileQuery.matches
  }

  initializeGrid() {
    if (!this.grid) return

    const storedMode = window.localStorage.getItem(GRID_STORAGE_KEY)
    const resolvedMode = this.normalizeGridMode(storedMode || this.currentMode)

    this.setGridMode(resolvedMode)
    window.addEventListener('featuredProducts.setColumns', this.onExternalSetColumnsBound as EventListener)
    this.mobileQuery.addEventListener('change', this.onViewportChangeBound)
  }

  normalizeGridMode(rawMode: string | GridMode, forceMobileView?: boolean): GridMode {
    const isMobileView = forceMobileView ?? this.isMobileView()

    if (rawMode === 'list') return 'list'

    if (isMobileView) {
      if (rawMode === '2' || rawMode === '1') return rawMode
      if (rawMode === '3') return '2'
      if (rawMode === '6') return '1'
    }
    else {
      if (rawMode === '3' || rawMode === '6') return rawMode
      if (rawMode === '2') return '3'
      if (rawMode === '1') return '6'
    }

    if (rawMode === 'L' || rawMode === 'l') return 'list'

    const parsedMode = Number.parseInt(String(rawMode || (isMobileView ? '2' : '6')), 10)

    if (isMobileView) {
      if (parsedMode === 2 || parsedMode === 3) return '2'
      if (parsedMode === 1 || parsedMode === 6) return '1'

      return '2'
    }

    if (parsedMode === 3 || parsedMode === 2) return '3'
    if (parsedMode === 6 || parsedMode === 1) return '6'

    return '6'
  }

  resolveColCount(mode: GridMode) {
    if (mode === 'list') return 1

    return Number(mode)
  }

  setGridMode(nextMode: GridMode) {
    if (!this.grid) return

    const hasModeApplied = this.grid.dataset.gridMode === nextMode

    if (this.currentMode === nextMode && hasModeApplied) return

    const normalizedMode = this.normalizeGridMode(nextMode)
    const colCount = this.resolveColCount(normalizedMode)

    this.currentMode = normalizedMode
    this.container.dataset.gridMode = normalizedMode

    this.grid.style.setProperty('--col-count', `${colCount}`)
    this.grid.style.setProperty('--list-mode', normalizedMode === 'list' ? '1' : '0')
    this.grid.dataset.gridMode = normalizedMode
  }

  onExternalSetColumns(e: CustomEvent<{ mode?: GridMode; cols?: number }>) {
    const nextMode = e.detail?.mode
      ? this.normalizeGridMode(e.detail.mode)
      : this.normalizeGridMode(String(e.detail?.cols || '6'))

    this.setGridMode(nextMode)
  }

  onViewportChange() {
    const storedMode = window.localStorage.getItem(GRID_STORAGE_KEY)

    this.setGridMode(this.normalizeGridMode(storedMode || this.currentMode))
  }
}
